import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { AppLayout } from './AppLayout';
import { RootLayout } from './RootLayout';
import { RouteError } from './RouteError';
import { useSession } from './session-context';

/**
 * Every screen is a lazy route so the initial bundle stays inside the budget in
 * docs/plan/17-pwa-mobile.md. Heavy dependencies (VexFlow, the sampler, the mic
 * worklet) load only with the routes that need them.
 */
const lazyDefault =
  (load: () => Promise<{ default: React.ComponentType }>) =>
  async (): Promise<{
    Component: React.ComponentType;
  }> => ({ Component: (await load()).default });

/** Sends first-time users through welcome, returning users to the path. */
function RootRedirect() {
  const { onboardingComplete } = useSession();
  return <Navigate to={onboardingComplete ? '/learn' : '/welcome'} replace />;
}

const routes: RouteObject[] = [
  { index: true, Component: RootRedirect },

  {
    path: 'welcome',
    lazy: lazyDefault(() => import('@/features/onboarding/WelcomeScreen')),
  },
  {
    path: 'onboarding',
    lazy: lazyDefault(() => import('@/features/onboarding/OnboardingScreen')),
  },
  {
    path: 'auth/signin',
    lazy: async () => {
      const { default: AuthScreen } = await import('@/features/auth/AuthScreen');
      return { Component: () => <AuthScreen mode="signin" /> };
    },
  },
  {
    path: 'auth/signup',
    lazy: async () => {
      const { default: AuthScreen } = await import('@/features/auth/AuthScreen');
      return { Component: () => <AuthScreen mode="signup" /> };
    },
  },

  // ---------------------------------------------------- tabbed app shell
  {
    Component: AppLayout,
    children: [
      { path: 'learn', lazy: lazyDefault(() => import('@/features/learn/LearnScreen')) },
      { path: 'practice', lazy: lazyDefault(() => import('@/features/practice/PracticeScreen')) },
      { path: 'songs', lazy: lazyDefault(() => import('@/features/songs/SongsScreen')) },
      { path: 'ensemble', lazy: lazyDefault(() => import('@/features/ensemble/EnsembleScreen')) },
      { path: 'profile', lazy: lazyDefault(() => import('@/features/profile/ProfileScreen')) },
      { path: 'settings', lazy: lazyDefault(() => import('@/features/settings/SettingsScreen')) },

      { path: 'quests', lazy: lazyDefault(() => import('@/features/quests/QuestsScreen')) },
      { path: 'streak', lazy: lazyDefault(() => import('@/features/streak/StreakScreen')) },
      {
        path: 'achievements',
        lazy: lazyDefault(() => import('@/features/quests/AchievementsScreen')),
      },
      { path: 'shop', lazy: lazyDefault(() => import('@/features/shop/ShopScreen')) },

      { path: 'wardrobe', lazy: lazyDefault(() => import('@/features/characters/WardrobeScreen')) },
      { path: 'friends', lazy: lazyDefault(() => import('@/features/friends/FriendsScreen')) },
      {
        path: 'u/:username',
        lazy: lazyDefault(() => import('@/features/friends/UserProfileScreen')),
      },
      { path: 'privacy', lazy: lazyDefault(() => import('@/features/about/PrivacyScreen')) },
      { path: 'credits', lazy: lazyDefault(() => import('@/features/about/CreditsScreen')) },
      {
        path: 'certificate',
        lazy: lazyDefault(() => import('@/features/certificate/CertificateScreen')),
      },
      {
        path: 'guidebook/:unitId',
        lazy: lazyDefault(() => import('@/features/guidebook/GuidebookScreen')),
      },
      {
        path: 'bond/:characterId',
        lazy: lazyDefault(() => import('@/features/characters/BondScreen')),
      },
    ],
  },

  // ------------------------------------------- full-screen (no tab bar)
  { path: 'tuner', lazy: lazyDefault(() => import('@/features/tuner/TunerScreen')) },
  {
    path: 'calibration',
    lazy: lazyDefault(() => import('@/features/calibration/CalibrationScreen')),
  },
  {
    path: 'placement',
    lazy: lazyDefault(() => import('@/features/placement/PlacementScreen')),
  },
  {
    path: 'testout/:section',
    lazy: lazyDefault(() => import('@/features/testout/TestOutScreen')),
  },
  { path: 'lesson/*', lazy: lazyDefault(() => import('@/features/lesson/LessonScreen')) },
  {
    path: 'practice/studio',
    lazy: lazyDefault(() => import('@/features/studio/StudioHub')),
  },
  {
    path: 'practice/studio/:tool',
    lazy: lazyDefault(() => import('@/features/studio/StudioRoute')),
  },
  {
    path: 'songs/:songId',
    lazy: lazyDefault(() => import('@/features/songs/SongDetailScreen')),
  },
  {
    path: 'songs/:songId/play',
    lazy: lazyDefault(() => import('@/features/songs/SongPlayerScreen')),
  },

  { path: '*', Component: RouteError },
];

export const router = createBrowserRouter([
  { path: '/', Component: RootLayout, ErrorBoundary: RouteError, children: routes },
]);
